import { ArrowRight, Sunset } from 'lucide-react';
import { Link } from 'react-router';
import { apiPaths, type SkyResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import type { SunsetLevel } from '../../../shared/domain/index.ts';
import { FRESHNESS_POLICIES } from '../../../shared/freshness/index.ts';
import { SectionCard } from '../../components/SectionCard.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { formatTime } from '../../lib/format.ts';

const SUNSET_LEVELS: Readonly<Record<SunsetLevel, { label: string; rank: number }>> = {
  dull: { label: 'Terne', rank: 1 },
  modest: { label: 'Discret', rank: 2 },
  good: { label: 'Beau', rank: 3 },
  great: { label: 'Spectaculaire', rank: 4 },
};

/** Potentiel en 4 pastilles (●●○○) ; le libellé porte l'information pour les lecteurs d'écran. */
export function SunsetDots({ level }: { level: SunsetLevel }) {
  const { rank, label } = SUNSET_LEVELS[level];
  return (
    <span className="sunset-dots">
      <span className="sunset-dots__dots" aria-hidden="true">
        {[1, 2, 3, 4].map((r) => (
          <span key={r} className={r <= rank ? 'on' : ''} />
        ))}
      </span>
      <span className="sunset-dots__label">{label}</span>
    </span>
  );
}

/** Version compacte du « ciel d'Ermont » pour l'accueil : l'essentiel, le détail est sur /ciel. */
export function SunsetCard() {
  const sky = useApi<SkyResponse>(apiPaths.sky(DEFAULT_CITY.slug), FRESHNESS_POLICIES.sky.refreshMs);
  const tomorrow = sky.data?.sky.data?.sunset?.day === 'tomorrow';
  return (
    <SectionCard
      title={tomorrow ? 'Le ciel demain soir' : 'Le ciel ce soir'}
      icon={<Sunset size={22} aria-hidden="true" />}
      kind="sky"
      subject="du ciel"
      section={sky.data?.sky}
      loading={sky.loading}
      fetchError={sky.error}
      skeleton={<Skeleton lines={3} />}
      className="sunset-card"
      actions={
        <Link className="text-link" to="/ciel">
          Voir le détail <ArrowRight size={16} aria-hidden="true" />
        </Link>
      }
    >
      {(outlook) =>
        outlook.sunset === null ? (
          <p className="state-message">Estimation du coucher de soleil indisponible.</p>
        ) : (
          <dl className="sunset-facts">
            <div>
              <dt>Coucher</dt>
              <dd>
                <time dateTime={outlook.sunset.sunsetAt}>{formatTime(outlook.sunset.sunsetAt)}</time>
              </dd>
            </div>
            <div>
              <dt>Potentiel coucher de soleil</dt>
              <dd>
                <SunsetDots level={outlook.sunset.level} />
              </dd>
            </div>
            <div>
              <dt>Heure dorée</dt>
              <dd>
                <time dateTime={outlook.sunset.goldenHourAt}>{formatTime(outlook.sunset.goldenHourAt)}</time>
                {' – '}
                <time dateTime={outlook.sunset.sunsetAt}>{formatTime(outlook.sunset.sunsetAt)}</time>
              </dd>
            </div>
          </dl>
        )
      }
    </SectionCard>
  );
}
